import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { axiosAuth } from "../../utils/axiosInstance";
import UserAccountLayout from "./userAccount/UserAccountLayout";
import BookingRow from "./userAccount/BookingRow";

const FILTERS = [
  { id: "All", label: "All" },
  { id: "Pending", label: "Pending" },
  { id: "Completed", label: "Completed" },
  { id: "Cancelled", label: "Cancelled" },
];

const Userbooking = () => {
  const [courtBookings, setCourtBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosAuth.get("my-bookings");
        if (response.status === 200) {
          const parsedBookings = [...response.data.bookings]
            .reverse()
            .map((booking) => {
              let parsedImages = [];
              try {
                parsedImages = booking.images ? JSON.parse(booking.images) : [];
              } catch (error) {
                console.error(
                  `Error parsing images for booking ${booking.bookingId}:`,
                  error,
                );
              }
              return {
                ...booking,
                courtName: booking.court,
                payment: booking.price,
                images: parsedImages,
              };
            });
          setCourtBookings(parsedBookings);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredCourtBookings = courtBookings.filter((booking) => {
    if (statusFilter === "All") return true;
    if (statusFilter === "Pending") return booking.status === "Pending";
    if (statusFilter === "Completed") return booking.status === "Success";
    if (statusFilter === "Cancelled") return booking.status === "cancelled";
    return false;
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCourtBookings.length / itemsPerPage),
  );

  const paginatedBookings = filteredCourtBookings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, itemsPerPage]);

  return (
    <UserAccountLayout
      active="bookings"
      title="My bookings"
      subtitle="Track and manage all your court reservations."
      actions={
        <Link to="/all-facilities" className="ua-btn ua-btn--primary">
          <i className="fa-solid fa-plus" aria-hidden="true" />
          New booking
        </Link>
      }
    >
      <div className="ua-filters" role="tablist" aria-label="Filter bookings">
        {FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            role="tab"
            aria-selected={statusFilter === filter.id}
            className={`ua-filter ${
              statusFilter === filter.id ? "is-active" : ""
            }`}
            onClick={() => setStatusFilter(filter.id)}
          >
            {filter.label}
          </button>
        ))}
      </div>

      <section className="ua-card">
        <div className="ua-card__head">
          <div>
            <h2>
              {statusFilter === "All"
                ? "All bookings"
                : `${FILTERS.find((f) => f.id === statusFilter)?.label} bookings`}
            </h2>
            <p>
              {filteredCourtBookings.length} booking
              {filteredCourtBookings.length === 1 ? "" : "s"} found
            </p>
          </div>
        </div>

        {loading ? (
          <div className="ua-loading">
            <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            Loading bookings…
          </div>
        ) : paginatedBookings.length > 0 ? (
          <>
            <div className="ua-bookings">
              {paginatedBookings.map((booking) => (
                <BookingRow key={booking.bookingId} booking={booking} />
              ))}
            </div>

            <div className="ua-pagination">
              <label className="ua-pagination__size">
                Show
                <select
                  value={itemsPerPage}
                  onChange={(e) =>
                    setItemsPerPage(
                      e.target.value === "-1"
                        ? filteredCourtBookings.length
                        : parseInt(e.target.value, 10),
                    )
                  }
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                per page
              </label>
              <div className="ua-pagination__nav">
                <button
                  type="button"
                  className="ua-pagination__btn"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                </button>
                <span className="ua-pagination__page">{currentPage}</span>
                <button
                  type="button"
                  className="ua-pagination__btn"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage >= totalPages}
                  aria-label="Next page"
                >
                  <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="ua-empty">
            <span className="ua-empty__icon" aria-hidden="true">
              <i className="fa-regular fa-calendar-xmark" />
            </span>
            <h3>No bookings found</h3>
            <p>
              {statusFilter === "All"
                ? "You haven't made any bookings yet."
                : `No ${statusFilter.toLowerCase()} bookings to show.`}
            </p>
            <Link to="/all-facilities" className="ua-btn ua-btn--primary">
              Book your first court
            </Link>
          </div>
        )}
      </section>
    </UserAccountLayout>
  );
};

export default Userbooking;
